import { createWeb3ReactRoot, Web3ReactProvider } from '@web3-react/core'
import 'inter-ui'
import React, { StrictMode } from 'react'
import { isMobile } from 'react-device-detect'
import ReactDOM from 'react-dom'
import ReactGA from 'react-ga'
import { Provider } from 'react-redux'
import { NetworkContextName } from './constants'
import './i18n'
import App from './pages/App'
import store from './state'
import ApplicationUpdater from './state/application/updater'
import ListsUpdater from './state/lists/updater'
import MulticallUpdater from './state/multicall/updater'
import TransactionUpdater from './state/transactions/updater'
import UserUpdater from './state/user/updater'
import ThemeProvider, { FixedGlobalStyle, ThemedGlobalStyle } from './theme'
import getLibrary from './utils/getLibrary'
import { injected } from './connectors'
import { useWeb3React } from '@web3-react/core'
import { ChainId } from '@uniswap/sdk'
import { useActiveWeb3React } from './hooks'
import { UnsupportedChainIdError } from '@web3-react/core'
import { initializeTokenList } from './services/tokenListApi'

const Web3ProviderNetwork = createWeb3ReactRoot(NetworkContextName)

if ('ethereum' in window) {
  ;(window.ethereum as any).autoRefreshOnNetworkChange = false
}

const GOOGLE_ANALYTICS_ID: string | undefined = process.env.REACT_APP_GOOGLE_ANALYTICS_ID
if (typeof GOOGLE_ANALYTICS_ID === 'string') {
  ReactGA.initialize(GOOGLE_ANALYTICS_ID)
  ReactGA.set({
    customBrowserType: !isMobile ? 'desktop' : 'web3' in window || 'ethereum' in window ? 'mobileWeb3' : 'mobileRegular'
  })
} else {
  ReactGA.initialize('test', { testMode: true, debug: true })
}

// 初始化允许的token列表
initializeTokenList().catch(error => {
  console.warn('Failed to initialize token list on startup:', error)
})

// 添加全局错误处理器 - 静默处理网络变化错误
window.addEventListener('unhandledrejection', event => {
  const error = event.reason
  // 检查是否是网络变化错误
  if (
    error &&
    ((error.message && error.message.includes('underlying network changed')) ||
      (error.message && error.message.includes('NETWORK_ERROR')) ||
      (error.message && error.message.includes('network changed')) ||
      (error.code && error.code === 'NETWORK_ERROR'))
  ) {
    console.debug('静默处理网络变化错误:', error)
    event.preventDefault() // 阻止错误显示
    return
  }
})

// 添加全局错误处理器 - 静默处理网络变化错误
window.addEventListener('error', event => {
  const error = event.error
  // 检查是否是网络变化错误
  if (
    error &&
    ((error.message && error.message.includes('underlying network changed')) ||
      (error.message && error.message.includes('NETWORK_ERROR')) ||
      (error.message && error.message.includes('network changed')))
  ) {
    console.debug('静默处理网络变化错误:', error)
    event.preventDefault() // 阻止错误显示
    return
  }
})

function useAutoConnect() {
  const { activate } = useWeb3React()
  const { chainId } = useActiveWeb3React()

  React.useEffect(() => {
    if (chainId == null) return

    // 支持的网络列表
    const supportedNetworks = [ChainId.K, ChainId.BSC]

    if (supportedNetworks.includes(chainId)) {
      // 如果当前网络是支持的，则激活连接
      activate(injected, undefined, true).catch(e => {
        if (e instanceof UnsupportedChainIdError) {
          console.warn('当前网络不支持，请切换到支持的网络')
        } else {
          throw e
        }
      })
    } else {
      console.warn('当前网络不支持，请切换到K链或BSC链')
    }
  }, [activate, chainId])
}

function Updaters() {
  useAutoConnect() // 挂载时自动连接
  return (
    <>
      <ListsUpdater />
      <UserUpdater />
      <ApplicationUpdater />
      <TransactionUpdater />
      <MulticallUpdater />
    </>
  )
}

ReactDOM.render(
  <StrictMode>
    <FixedGlobalStyle />
    <Web3ReactProvider getLibrary={getLibrary}>
      <Web3ProviderNetwork getLibrary={getLibrary}>
        <Provider store={store}>
          <Updaters />
          <ThemeProvider>
            <ThemedGlobalStyle />
            <App />
          </ThemeProvider>
        </Provider>
      </Web3ProviderNetwork>
    </Web3ReactProvider>
  </StrictMode>,
  document.getElementById('root')
)
